import openai from './openaiClient';
import {
  APIConnectionError,
  AuthenticationError,
  PermissionDeniedError,
  RateLimitError,
  InternalServerError
} from 'openai';

/**
 * AI Help Assistant Service
 * Provides contextual guidance and support for users navigating the GymFlow platform
 */
export const aiHelpService = {
  /**
   * Generates contextual help based on user's current page and question
   * @param {string} pageContext - Current page/feature user is on
   * @param {string} userQuestion - User's question or help request
   * @param {Object} additionalContext - Optional additional context (user role, gym info, etc.)
   * @returns {Promise<Object>} AI-generated help response
   */
  async getContextualHelp(pageContext, userQuestion, additionalContext = {}) {
    try {
      const systemPrompt = `You are a helpful gym management system assistant. You provide clear, concise, and actionable guidance to users based on their current context within the application.

Your responses should:
- Be specific to the feature/page they're using
- Provide step-by-step instructions when applicable
- Be friendly and encouraging
- Keep responses under 200 words
- Focus on practical solutions

Current context: ${pageContext}
User role: ${additionalContext?.userRole || 'member'}
Gym: ${additionalContext?.gymName || 'N/A'}`;

      const response = await openai?.chat?.completions?.create({
        model: 'gpt-5-mini',
        messages: [
          {
            role: 'system',
            content: systemPrompt
          },
          {
            role: 'user',
            content: userQuestion
          }
        ],
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'help_response',
            schema: {
              type: 'object',
              properties: {
                answer: { type: 'string' },
                steps: {
                  type: 'array',
                  items: { type: 'string' }
                },
                relatedFeatures: {
                  type: 'array',
                  items: { type: 'string' }
                },
                tips: {
                  type: 'array',
                  items: { type: 'string' }
                }
              },
              required: ['answer'],
              additionalProperties: false
            }
          }
        },
        reasoning_effort: 'minimal',verbosity: 'low'
      });

      return JSON.parse(response?.choices?.[0]?.message?.content);
    } catch (error) {
      const errorInfo = getErrorMessage(error);
      if (errorInfo?.isInternal) {
        console.log(errorInfo?.message);
      } else {
        console.error('Error generating help response:', error);
      }
      throw new Error(errorInfo.message);
    }
  },

  /**
   * Generates quick help suggestions based on page context
   * @param {string} pageContext - Current page/feature
   * @param {string} userRole - User's role in the system
   * @returns {Promise<Array>} Array of suggested questions
   */
  async getSuggestedQuestions(pageContext, userRole) {
    try {
      const response = await openai?.chat?.completions?.create({
        model: 'gpt-5-mini',
        messages: [
          {
            role: 'system',
            content: `Generate 4 common questions a ${userRole} might have while using the ${pageContext} feature of a gym management system. Keep questions concise and practical.`
          },
          {
            role: 'user',
            content: 'Generate suggested questions'
          }
        ],
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'suggested_questions',
            schema: {
              type: 'object',
              properties: {
                questions: {
                  type: 'array',
                  items: { type: 'string' },
                  minItems: 4,
                  maxItems: 4
                }
              },
              required: ['questions'],
              additionalProperties: false
            }
          }
        },
        reasoning_effort: 'minimal',
        verbosity: 'low'
      });

      const result = JSON.parse(response?.choices?.[0]?.message?.content);
      return result?.questions || [];
    } catch (error) {
      const errorInfo = getErrorMessage(error);
      console.error('Error generating suggested questions:', errorInfo?.message);
      return [
        'How do I get started with this feature?',
        'What are the main functions available here?',
        'Can you explain how this works?',
        'What should I do next?'
      ];
    }
  }
};

/**
 * Helper function to get error messages (imported from openaiService pattern)
 */
function getErrorMessage(error) {
  if (error instanceof AuthenticationError) {
    return { isInternal: true, message: 'Invalid API key or authentication failed. Please check your OpenAI API key.' };
  } else if (error instanceof PermissionDeniedError) {
    return { isInternal: true, message: 'Quota exceeded or authorization failed. You may have exceeded your usage limits.' };
  } else if (error instanceof RateLimitError) {
    return { isInternal: true, message: 'Rate limit exceeded. Please wait a moment and try again.' };
  } else if (error instanceof InternalServerError) {
    return { isInternal: true, message: 'OpenAI service is currently unavailable. Please try again later.' };
  } else if (error instanceof APIConnectionError) {
    return { isInternal: true, message: 'Unable to connect to OpenAI service. Please check your internet connection.' };
  } else {
    return { isInternal: false, message: error?.message || 'An unexpected error occurred. Please try again.' };
  }
}